import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';

import {
  useAlphabeticalSortedPetsWithPolicy,
  useBackButton,
  useDirectCancel,
  useMixpanel,
  usePetcloudCCProp103,
  usePMS,
} from '../../../hooks';

import { usePetPolicies } from '../../../hooks/usePetPolicies';

import figoLogo from '../../../assets/figo-logo-white.svg';

import {
  billingPaymentReseted,
  clearPolicyNotifications,
  getChangeCoveragePreview,
  isFirstTimeOnClaims,
  petSelected,
  policySelected,
  policySelectedOnClaims,
  restartRate,
  reviewChangesVisibleChanged,
  setSelectedPetId,
  setSelectedPetIndex,
  validateChangeCoverage,
  validateChangeCoverageCleared,
} from '../../../actions';

import {
  CHANGE_COVERAGE_WEB,
  INSURANCE,
} from '../../navigation/routes.constants';

import { EV1 } from '../../../services/mixpanel';

import { PetCloudBreadcrumbs } from '../../myPets/common/PetCloudBreadcrumbs';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import Shimmer from '../../common/Shimmer';

import { ChangeCoverageScreenPreview, VETERINARY_EXAM_FEES_PSM_ID, WELLNESS_PSM_ID } from './ChangeCoverageScreenPreview';
import {
  ChangeCoverageScreenOptions,
} from './changeCoverageOptions/ChangeCoverageScreenOptions';
import { CancelRenewalSent } from './changeCoverageOptions/CancelRenewalSent';
import {
  CancelRenewalSuccess,
} from './changeCoverageOptions/CancelRenewalSuccess';
import { usePolicyBrand } from '../../../hooks/usePolicyBrand';

import './css/ChangeCoverageInsuranceScreen.css';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';

const ChangeCoverageInsuranceScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const { sendEvent } = useMixpanel();
  const { t } = useTranslation('changeCoverage');
  const store = useSelector(({ policies }) => ({ policies }));
  const { policyNumber } = useParams();
  const { policyBrandName } = usePolicyBrand({ policyNumber });
  const { hasPets } = usePetPolicies();
  const { pms } = usePMS();
  const sortedPetsWithPolicy =
    useAlphabeticalSortedPetsWithPolicy();
  const showDirectCancel = useDirectCancel();
  const useCCProp103 = usePetcloudCCProp103();

  const {
    policies: {
      allPets,
      allPolicies,
      changeCoverage,
      changeCoverage: {
        cancelMultibrandPolicy,
        cancelRenewalRequestSent,
        preview,
        reviewChangesVisible,
        success,
      },
      changeCoverageValidation,
      loading: policiesLoading,
    },
  } = store;
  const location = useLocation();
  const isOptionsRoute = location.pathname.endsWith('/options');
  const titleKey = isOptionsRoute
    ? 'preview.changeCoverage'
    : 'preview.renewalPreview';

  const isPolicyOwner = useMemo(() => {
    let isOwner = false;

    if (allPolicies && policyNumber) {
      isOwner = !!allPolicies
        .find((policy) => policy.Policy.Number === policyNumber);
    }

    return isOwner;
  }, [allPolicies, policyNumber]);

  const powerUps = useMemo(() => [
    {
      detail: preview.VeterinaryInformation,
      id: VETERINARY_EXAM_FEES_PSM_ID,
      included: preview.HaveVeterinaryFees,
      title: t('preview.vetFees'),
    },
    {
      detail: preview.WellnessInformation,
      id: WELLNESS_PSM_ID,
      included: preview.HaveWellness,
      title: t('preview.wellness'),
    },
  ], [preview]);

  useBackButton(() => {
    dispatch(restartRate());
  }, []);

  useEffect(() => () => { dispatch(restartRate()); }, [dispatch]);

  useEffect(() => {
    if (store.policies.loading) {
      return;
    }

    if (isPolicyOwner) {
      dispatch(validateChangeCoverage({ policyNumber }));
    } else {
      const source = window.location.pathname;
      sendEvent(EV1({ pms, policyBrand: policyBrandName, source }));
      history.push(INSURANCE);
    }
  }, [dispatch, isPolicyOwner, policyNumber, store.policies.loading]);

  function handlePolicySelect(index) {
    const { policies } = store;

    dispatch(clearPolicyNotifications());

    if (!policies.allPolicies.length) {
      return;
    }

    dispatch(policySelected(index));
    dispatch(policySelectedOnClaims(index + 1));
    dispatch(isFirstTimeOnClaims(false));
  }

  function handlePetSelect(index) {
    const petId = allPets[index].PetId;
    const indexBillingPayment = sortedPetsWithPolicy
      .findIndex((pet) => pet.PetId === petId);
    const indexNotFound = -1;
    const firstIndex = 0;
    dispatch(petSelected(indexBillingPayment === indexNotFound
      ? firstIndex
      : indexBillingPayment));
    dispatch(setSelectedPetId(petId));
    dispatch(clearPolicyNotifications());
    dispatch(setSelectedPetIndex(index));
    dispatch(billingPaymentReseted());
  }

  const previewData = useMemo(() => {
    // WIP prop103: only for testing
    const CoverageName = 'Accident Only';

    if (useCCProp103) {
      return {
        ...preview,
        CoverageName,
      };
    }

    return preview;
  }, [preview]);

  useEffect(() => {
    if (policiesLoading || !changeCoverageValidation.isSuccess) {
      return;
    }

    if (hasPets) {
      const petIndexToSelect = sortedPetsWithPolicy
        .findIndex((pet) => pet.policies
          .find((policy, index) => {
            const policyFound = policy.Number === policyNumber;
            if (policyFound) {
              handlePolicySelect(index);
            }
            return policyFound;
          }));

      if (petIndexToSelect < 0
        || (changeCoverageValidation.isSuccess
          && !changeCoverageValidation.isValid)) {
        const source = window.location.pathname;
        sendEvent(EV1({ pms, policyBrand: policyBrandName, source }));
        history.replace(INSURANCE);
      } else {
        handlePetSelect(petIndexToSelect);
        dispatch(validateChangeCoverageCleared());
        dispatch(getChangeCoveragePreview({ policyNumber }));
      }
    }
  }, [
    dispatch,
    changeCoverageValidation.isSuccess,
    changeCoverageValidation.isValid,
    policyNumber,
    sortedPetsWithPolicy,
    hasPets]);

  function shouldPromptWarning(_crntLocation, nextLocation) {
    const { isSuccess, isValid } = changeCoverageValidation;

    if (nextLocation.pathname === INSURANCE && success) {
      return false;
    }

    return ((!isSuccess || isValid)
      && nextLocation.pathname !== `${CHANGE_COVERAGE_WEB}/${policyNumber}`);
  }

  function handleBackButton() {
    if (reviewChangesVisible) {
      dispatch(reviewChangesVisibleChanged(false));
      return;
    }

    history.goBack();
  }

  function handleChangeCoverageButton() {
    history.push(`${CHANGE_COVERAGE_WEB}/${policyNumber}/options`);
  }

  function handleKeepCoverageButton() {
    const findPetId =
      allPolicies.find((policy) => policy.Policy.Number === policyNumber);
    const source = window.location.pathname;
    sendEvent(EV1({ pms, policyBrand: policyBrandName, source }));
    const url = findPetId
      ? `${INSURANCE}/${findPetId?.PetInformation?.PetId}` : INSURANCE;
    history.push(url);
    window.scrollTo(0, 0);
  }

  function renderChangeCoverageContainer() {
    if (policiesLoading || changeCoverageValidation.isLoading) {
      return (
        <Shimmer
          className="Change-coverage-insurance-card-container-shimmer"
        />
      );
    }

    return (
      <div className={'Change-coverage-insurance-card-container '
        + `${reviewChangesVisible
          ? 'Change-coverage-insurance-container-mobile' : ''}`}
      >
        <Switch>
          <Route path={`${CHANGE_COVERAGE_WEB}/:policyNumber/options`}>
            <ChangeCoverageScreenOptions
              onRateCreationError={handleBackButton}
              policyNumber={policyNumber}
              validatePromptWarning={shouldPromptWarning}
            />
          </Route>

          <Route exact path={match.path}>
            <ChangeCoverageScreenPreview
              loading={changeCoverage.loading}
              marketingChannel={MARKETING_CHANNEL_ID.figo}
              onChangeCoverageClick={handleChangeCoverageButton}
              onKeepClick={handleKeepCoverageButton}
              policyNumber={policyNumber}
              powerUps={powerUps}
              preview={previewData}
            />
          </Route>
        </Switch>
      </div>
    );
  }

  if (
    showDirectCancel
    && !cancelMultibrandPolicy.loading
    && cancelMultibrandPolicy.success
  ) {
    return (
      <CancelRenewalSuccess
        onReturnClick={handleKeepCoverageButton}
        renewalDate={preview?.NewPremiumEffectiveDate}
      />
    );
  }

  if (!showDirectCancel && cancelRenewalRequestSent) {
    return <CancelRenewalSent onReturnClick={handleKeepCoverageButton} />;
  }

  return (
    <div className="Change-coverage-insurance-screen-container">
      <PetCloudBreadcrumbs
        activeRoute={t(titleKey)}
        rootRoute={t('insuranceTitle')}
      />

      <h1>{t(titleKey)}</h1>

      <div className="Change-coverage-insurance-header-container">
        <img
          alt=""
          className="Change-coverage-insurance-header-logo"
          src={figoLogo}
        />
      </div>

      {renderChangeCoverageContainer()}

      <FigoLoadingOverlay
        visible={policiesLoading || changeCoverageValidation.isLoading}
      />
    </div>
  );
};

export { ChangeCoverageInsuranceScreen };
